/**
 * Продуктовая аналитика мини-приложения: буфер в памяти + отправка батчами на users-service.
 * Эндпоинт: POST {getStatsApiBase()}/miniapp/product-analytics/batch
 */

import { fetchWithAuthRelogin } from './sessionRelogin';
import { getMiniappAccessToken } from './miniappAccessToken';
import { getStatsApiBase } from './miniappAdminOnly';
import { isTelegramWebAppEnvironment } from './telegramEnvironment';

const STORAGE_SESSION_KEY = 'mw_product_analytics_session_v1';
const FLUSH_INTERVAL_MS = 60_000;
const MAX_QUEUE = 400;
const MAX_STRING_PROP = 500;

let queue = [];
let flushTimer = null;
let started = false;

function newId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) {
    /* ignore */
  }
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function getClientSessionId() {
  try {
    let id = sessionStorage.getItem(STORAGE_SESSION_KEY);
    if (!id) {
      id = newId();
      sessionStorage.setItem(STORAGE_SESSION_KEY, id);
    }
    return id;
  } catch (_) {
    return newId();
  }
}

function sanitizeValue(v, depth) {
  if (depth > 6) return null;
  if (v == null) return v;
  const t = typeof v;
  if (t === 'string') return v.length > MAX_STRING_PROP ? `${v.slice(0, MAX_STRING_PROP)}…` : v;
  if (t === 'number' && Number.isFinite(v)) return v;
  if (t === 'boolean') return v;
  if (Array.isArray(v)) {
    return v.slice(0, 40).map((x) => sanitizeValue(x, depth + 1));
  }
  if (t === 'object') {
    const out = {};
    const keys = Object.keys(v).slice(0, 40);
    for (const k of keys) {
      if (k.length > 64) continue;
      out[k] = sanitizeValue(v[k], depth + 1);
    }
    return out;
  }
  return String(v).slice(0, 120);
}

/**
 * @param {string} name
 * @param {Record<string, unknown>} [props]
 */
export function track(name, props = {}) {
  if (!name || typeof name !== 'string') return;
  const n = name.trim().slice(0, 128);
  if (!n) return;
  const row = {
    name: n,
    t_ms: Date.now(),
    props: props && typeof props === 'object' ? sanitizeValue(props, 0) : {},
  };
  queue.push(row);
  if (queue.length > MAX_QUEUE) {
    queue = queue.slice(-MAX_QUEUE);
  }
}

async function sendBatch(events) {
  const token = getMiniappAccessToken();
  if (!token || !events.length) return false;
  const batchId = newId();
  const url = `${getStatsApiBase()}/miniapp/product-analytics/batch`;
  const body = JSON.stringify({
    batch_id: batchId,
    client_session_id: getClientSessionId(),
    client_sent_at_ms: Date.now(),
    events: events.map((e) => ({
      name: e.name,
      t_ms: e.t_ms,
      props: e.props && typeof e.props === 'object' ? e.props : {},
    })),
  });
  const res = await fetchWithAuthRelogin(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body,
  });
  return res.ok;
}

export async function flushProductAnalytics() {
  if (!queue.length) return;
  if (!getMiniappAccessToken()) {
    queue = [];
    return;
  }
  const snapshot = queue;
  queue = [];
  try {
    await sendBatch(snapshot);
  } catch (_) {
    queue = snapshot.concat(queue);
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
  }
}

function platform() {
  return isTelegramWebAppEnvironment() ? 'telegram' : 'browser';
}

function onVisibility() {
  if (document.visibilityState === 'hidden') {
    flushProductAnalytics();
  }
}

/**
 * Вызывать один раз при старте приложения (после инициализации роутера).
 */
export function initProductAnalytics() {
  if (started) return;
  started = true;
  track('app_open', { platform: platform() });
  flushTimer = window.setInterval(() => {
    flushProductAnalytics();
  }, FLUSH_INTERVAL_MS);
  window.addEventListener('pagehide', flushProductAnalytics);
  document.addEventListener('visibilitychange', onVisibility);
}

export function shutdownProductAnalytics() {
  if (flushTimer != null) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  window.removeEventListener('pagehide', flushProductAnalytics);
  document.removeEventListener('visibilitychange', onVisibility);
  started = false;
}
